import {
  enableProdMode,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { CUSTOM_DATE_FORMAT } from './app/custom-date-picker-formats';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { DatePipe } from '@angular/common';
import { GlobalErrorHandler, InactivityMonitorService } from '@eps/ui-components';
import { AppConfigService } from './app/app-config.service';
import {
  preloadActiveLanguage,
  translocoConfiguration,
  TranslocoHttpLoader,
} from './app/core/app-config/transloco/transloco.config';
import { provideTransloco } from '@jsverse/transloco';
import { provideRouter } from '@angular/router';
import { appRoutes } from './app/core/app-config/routing/app.routes';
import { httpErrorInterceptor } from './app/core/interceptors/http-error.interceptor';
import { authenticationInterceptor } from './app/core/interceptors/authentication.interceptor';
import { loadingOverlayInterceptor } from './app/core/interceptors/loading-overlay.interceptor';
import { applicationAccessInterceptor } from './app/core/interceptors/application-access.interceptor';
import { feedbackDiagnosticsInterceptor } from './app/core/interceptors/feedback-diagnostics.interceptor';
import { FeedbackErrorHandler } from './app/core/error-handlers/feedback-error-handler';
import { FeedbackClientService } from './app/shared/services/feedback-client.service';
import { provideAuth } from './app/core/auth';
import { AuthStoreService } from './app/shared/store/auth-store.service';
import { BASE_PATH } from './app/api/swagger/variables';
import { AppConfig } from './app/shared/app-config';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(appRoutes),
    provideZonelessChangeDetection(),
    AppConfigService,
    provideAppInitializer(async () => {
      const appConfigService = inject(AppConfigService);
      const authStoreService = inject(AuthStoreService);

      await appConfigService.loadAppConfig();
      authStoreService.watchProviderSession();
    }),
    provideAuth(),
    // Remove when you do not want/need an inactivity service, which redirects the user to a page after a certain amount of time.
    provideAppInitializer(() => {
      inject(InactivityMonitorService);
    }),
    // Starts the asynchronous client hint lookup early so feedback reports carry the precise names.
    provideAppInitializer(() => {
      inject(FeedbackClientService);
    }),
    // Remove when you do not want/need a global error handler
    GlobalErrorHandler,
    {
      provide: ErrorHandler,
      useClass: FeedbackErrorHandler,
    },
    DatePipe,
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    // Render CDK overlays via the overlay container instead of the native popover API,
    // otherwise autocomplete panels are mispositioned/clipped when opened inside a dialog.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideHttpClient(
      withXhr(),
      withInterceptors([
        authenticationInterceptor,
        applicationAccessInterceptor,
        loadingOverlayInterceptor,
        httpErrorInterceptor,
        feedbackDiagnosticsInterceptor,
      ])
    ),
    {
      provide: BASE_PATH,
      useFactory: () => (AppConfig.api?.url ?? 'http://localhost').replace(/\/+$/, ''),
    },
    provideAnimations(),
    provideTransloco({
      config: translocoConfiguration,
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(preloadActiveLanguage),
  ],
}).catch((err) => console.error(err));
